import { useEffect } from 'react';

/**
 * SEO Component for dynamic meta tags
 * Usage: <SEO title="Page Title" description="Page description" />
 */
export default function SEO({ 
  title = "Nouveau Riche Legacy", 
  description = "Build Your Financial Dynasty with comprehensive wealth-building tools, education, and consulting.",
  image = "https://nouveaurichelegacy.com/og-image.png",
  url = "",
  type = "website"
}) {
  useEffect(() => {
    // Update document title
    document.title = title.includes("Nouveau Riche") ? title : `${title} | Nouveau Riche Legacy`;
    
    // Update meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };
    
    // Basic meta tags
    updateMetaTag('description', description);
    
    // Open Graph
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:type', type, true);
    if (url) updateMetaTag('og:url', url, true);
    
    // Twitter
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', image, true);
    
    // Track page view in Google Analytics
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: title,
        page_location: window.location.href
      });
    }
    
  }, [title, description, image, url, type]);
  
  return null; // This component doesn't render anything
}

// Analytics helper functions
export const trackEvent = (eventName, eventParams = {}) => {
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
  if (window.fbq) {
    window.fbq('track', eventName, eventParams);
  }
};

// Common events
export const trackSignUp = () => trackEvent('sign_up', { method: 'email' });
export const trackLogin = () => trackEvent('login', { method: 'email' });
export const trackPurchase = (value, currency = 'USD', itemName = '') => {
  trackEvent('purchase', { value, currency, item_name: itemName });
};
export const trackBeginCheckout = (value, currency = 'USD') => {
  trackEvent('begin_checkout', { value, currency });
};
export const trackViewItem = (itemName, itemCategory = '') => {
  trackEvent('view_item', { item_name: itemName, item_category: itemCategory });
};
export const trackSearch = (searchTerm) => {
  trackEvent('search', { search_term: searchTerm });
};

// Page-specific SEO configurations
export const SEO_CONFIG = {
  home: {
    title: "Nouveau Riche Legacy | Build Your Financial Dynasty",
    description: "Transform your financial future with NRL's comprehensive wealth-building platform. Access trading tools, education courses, AI-powered insights, and expert consulting services."
  },
  trading: {
    title: "Trading Platform | Stocks, Crypto, Forex",
    description: "Trade stocks, cryptocurrency, and forex with our advanced trading platform. TradingView charts, real-time data, and portfolio analytics."
  },
  education: {
    title: "Gilded Study | Financial Education Hub",
    description: "Master trading and investing with comprehensive courses on stocks, crypto, forex, credit building, and wealth management strategies."
  },
  aiAssistant: {
    title: "AI Trade Assistant | 24/7 Financial Insights",
    description: "Get personalized trading insights, market analysis, and financial guidance from our AI-powered assistant available around the clock."
  },
  booking: {
    title: "Consultation Services | Expert Financial Coaching",
    description: "Book one-on-one sessions with wealth-building experts. From free consultations to intensive coaching programs."
  },
  copyTrading: {
    title: "Copy Trading | Follow Top Traders",
    description: "Automatically copy trades from successful traders. View leaderboards, track performance, and learn from the best."
  },
  staking: {
    title: "Staking | Earn Passive Income",
    description: "Stake your crypto assets and earn passive income. Multiple pools, competitive APY rates, and flexible terms."
  },
  realEstate: {
    title: "Real Estate REITs | Property Investment",
    description: "Invest in real estate through REITs. Access property markets, track performance, and build wealth through real estate."
  },
  web3: {
    title: "Web3 Services | Blockchain Solutions",
    description: "Explore Web3 services from our partner ecosystem. DeFi, blockchain development, and decentralized finance solutions."
  },
  subscription: {
    title: "Membership Plans | NRL University",
    description: "Choose your wealth-building pathway. From free exploration to Dynasty Builder with full trading access and coaching."
  },
  about: {
    title: "About Us | Our Mission",
    description: "Learn about Nouveau Riche Legacy's mission to democratize wealth-building through education, technology, and community."
  }
};
